import { call, delay, put, select, takeLatest } from "redux-saga/effects";

import { summarizeKnowledgeBases } from "../../data/api/botpressKnowledgeBaseSync.js";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import { createOmniPaginationObject } from "../../utils/helpers";
import { deepCopyObject, helperGetIsoDate } from "../../utils/helpers.js";
import * as UserSessionStorage from "../../utils/userSessionStorage";
import * as Actions from "../redux/botpressSession";

const getDefaultPagination = () => {
  let newPage = createOmniPaginationObject(null, 20);
  newPage.pageIndex = 0;
  newPage.sort = { createdAt: "desc" };
  return newPage;
};

const mapAssessmentStatusToFeedbackStatus = assessmentStatus => {
  switch (assessmentStatus) {
    case 0:
      return 2; // New assessment -> feedback Open (moved off "New")
    case 1:
      return 6; // Pass -> Fixed in Test (Closed once the fix is deployed)
    case 2:
      return 4; // Fail -> Retest
    case 3:
      return 4; // Retest -> Retest
    default:
      return null;
  }
};

// Looks up feedback #feedbackId in one bot's FeedbackTable. When the assessment
// knows which conversation it was rerun from, the feedback must belong to that
// conversation; row ids are per-table, so the same id in the other bot's table
// is a different, unrelated record.
function* findLinkedFeedback(botODM, feedbackId, originalConversationId) {
  if (botODM == null) {
    return null;
  }
  const lookupPage = getDefaultPagination();
  lookupPage.pageSize = 1;
  lookupPage.query = { id: feedbackId };
  lookupPage.botODM = botODM;

  const feedbackResult = yield call(BotpressAPI.searchFeedback, lookupPage);
  const feedbackRecord = feedbackResult?.rows?.[0];
  if (feedbackRecord == null) {
    return null;
  }
  if (originalConversationId && feedbackRecord.conversationId !== originalConversationId) {
    return null;
  }
  return feedbackRecord;
}

// Mirrors an assessment's status onto the feedback it was created from. Runs on
// create and whenever the status changes, so later edits (e.g. saving Compare
// results) don't overwrite a status someone has since set by hand.
function* syncFeedbackFromAssessment(assessmentRecord, previousStatus) {
  try {
    const feedbackId = assessmentRecord?.feedbackId;
    if (feedbackId == null) {
      return;
    }
    if (assessmentRecord.id != null && assessmentRecord.status === previousStatus) {
      return;
    }

    const targetStatus = mapAssessmentStatusToFeedbackStatus(assessmentRecord.status);
    if (targetStatus == null) {
      return;
    }

    // Reruns launched from Production Feedback link a PROD feedback record;
    // ones launched from Testing Feedback link a TEST one. Try PROD first.
    const prodBotODM = yield select(Actions.botpressBotODMSelector);
    const testBotODM = yield select(Actions.testBotODMSelector);
    const originalConversationId = assessmentRecord.originalConversationId;

    let feedbackBotODM = prodBotODM;
    let feedbackRecord = yield* findLinkedFeedback(prodBotODM, feedbackId, originalConversationId);
    if (
      feedbackRecord == null &&
      testBotODM != null &&
      testBotODM.omniData.botId !== prodBotODM?.omniData.botId
    ) {
      feedbackBotODM = testBotODM;
      feedbackRecord = yield* findLinkedFeedback(testBotODM, feedbackId, originalConversationId);
    }
    if (feedbackRecord == null) {
      console.warn(`syncFeedbackFromAssessment: feedback #${feedbackId} not found; skipping.`);
      return;
    }

    if (feedbackRecord.status === targetStatus) {
      return;
    }

    const updatedFeedback = { ...feedbackRecord, status: targetStatus };
    const updateReturn = yield call(BotpressAPI.updateFeedback, {
      botODM: feedbackBotODM,
      feedbackRecords: [updatedFeedback],
    });
    if (updateReturn && Object.hasOwn(updateReturn, "code")) {
      console.error("syncFeedbackFromAssessment: feedback update failed", updateReturn);
      return;
    }

    // Refresh whichever feedback widget shows the record we just changed. When
    // the PROD and TEST bots are the same bot, both widgets show it.
    const feedbackBotId = feedbackBotODM.omniData.botId;
    if (prodBotODM?.omniData.botId === feedbackBotId) {
      const feedbackPage = deepCopyObject(yield select(Actions.feedbackPaginationSelector));
      feedbackPage["botODM"] = prodBotODM;
      feedbackPage["invalidateCache"] = true;
      yield put(Actions.searchFeedbackRequest(feedbackPage));
    }
    if (testBotODM?.omniData.botId === feedbackBotId) {
      const testFeedbackPage = deepCopyObject(yield select(Actions.testFeedbackPaginationSelector));
      testFeedbackPage["botODM"] = testBotODM;
      testFeedbackPage["invalidateCache"] = true;
      yield put(Actions.searchTestFeedbackRequest(testFeedbackPage));
    }
  } catch (error) {
    console.error("syncFeedbackFromAssessment: unexpected error", error);
  }
}

export function* setBotpressBotODM({ payload }) {
  try {
    //get the current and clear it out

    if (payload != null) {
      const botId = payload.omniData.botId;
      yield call(UserSessionStorage.storeLastBotpressBotID, botId);
    }

    yield put(Actions.setBotpressBotODMSuccess(payload));

    //The subsequent methods may not have the state/date set yet, so we grab it ourselves
    const startDate = yield select(Actions.analyticsStartDateSelector);
    let theParameters = {
      botODM: payload,
      analyticsStartDate: startDate,
    };
    yield put(Actions.loadAnalyticsRequest(theParameters));
    yield put(Actions.loadBotRequest(theParameters));
    yield put(Actions.loadBotpressSummaryRequest(theParameters));
    yield put(Actions.loadConfigurationsRequest(theParameters));

    // //Tables load this via pagination when needed
    // const chatPage = yield select(Actions.chatPaginationSelector);
    // chatPage["botODM"] = payload;
    // yield put(Actions.searchChatsRequest(chatPage));

    //load feedback
    const feedbackPage = yield select(Actions.feedbackPaginationSelector);
    feedbackPage["botODM"] = payload;
    yield put(Actions.searchFeedbackRequest(feedbackPage));

    //load products
    const productPage = yield select(Actions.productPaginationSelector);
    productPage["botODM"] = payload;
    yield put(Actions.searchProductsRequest(productPage));

    //load members
    const memberPage = yield select(Actions.memberPaginationSelector);
    memberPage["botODM"] = payload;
    yield put(Actions.searchMembersRequest(memberPage));

    //load presented links
    const presentedLinkPage = yield select(Actions.presentedLinkPaginationSelector);
    presentedLinkPage["botODM"] = payload;
    yield put(Actions.searchPresentedLinksRequest(presentedLinkPage));

    //load knowledge base scraped website urls
    yield put(
      Actions.loadKnowledgeBaseUrlsRequest({
        botODM: payload,
      }),
    );

    //load knowledge base markdown files
    yield put(
      Actions.loadKnowledgeBaseMarkdownFilesRequest({
        botODM: payload,
      }),
    );
  } catch (error) {
    yield put(Actions.loadAnalyticsRequest(null));
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* setAnalyticsBotODM({ payload }) {
  try {
    //get the current and clear it out
    yield put(Actions.setAnalyticsBotODMSuccess(payload));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* setTestBotODM({ payload }) {
  try {
    //get the current and clear it out
    yield put(Actions.setTestBotODMSuccess(payload));

    //load assessment (relative to the test bot)
    const assessmentPage = yield select(Actions.assessmentPaginationSelector);
    assessmentPage["botODM"] = payload;
    yield put(Actions.searchAssessmentRequest(assessmentPage));

    //load the test bot's configuration (used to stamp assessment versions)
    yield put(Actions.loadTestConfigurationsRequest({ botODM: payload }));

    //load the test bot's knowledge base markdown files (FAQ widget)
    yield put(Actions.loadTestKnowledgeBaseMarkdownFilesRequest({ botODM: payload }));

    yield put(Actions.loadTestKnowledgeBasesRequest({ botODM: payload }));

    //load the test bot's feedback ("Test Feedback" widget)
    const testFeedbackPage = yield select(Actions.testFeedbackPaginationSelector);
    testFeedbackPage["botODM"] = payload;
    yield put(Actions.searchTestFeedbackRequest(testFeedbackPage));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* loadAnalytics({ payload }) {
  try {
    const yesterdayEverythingSeemedSoFarAway = new Date(helperGetIsoDate(1, true, true)); //today but start of the day
    //All this shit is to handler initialization, you can pass in empty payloads after setting the bot
    const startDate =
      payload.analyticsStartDate != null
        ? new Date(payload.analyticsStartDate)
        : yesterdayEverythingSeemedSoFarAway;
    const currentAnalytics = yield select(Actions.analyticsSelector);
    const currentStartDate = yield select(Actions.analyticsStartDateSelector);

    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (botpressBotODM == null) {
      yield put(
        Actions.loadAnalyticsSuccess({
          analytics: null,
          analyticsStartDate: startDate,
        }),
      );
      return;
    }

    //first load so just get it, it will be the 30 days....
    if (!currentAnalytics) {
      //today
      const endDate = new Date(helperGetIsoDate(0, false, true)); //today

      let theParameters = {
        botODM: botpressBotODM,
        analyticsStartDate: startDate,
        analyticsEndDate: endDate,
      };
      let theReturn = yield call(BotpressAPI.getBotAnalytics, theParameters);

      if (theReturn) {
        yield put(
          Actions.loadAnalyticsSuccess({
            analytics: theReturn.records,
            analyticsStartDate: startDate,
          }),
        );
      }
    } else if (startDate < currentStartDate) {
      const currentAnalytics = yield select(Actions.analyticsSelector);
      let newAnalytics = currentAnalytics.slice();

      let newEndDate = new Date(currentStartDate.getTime());
      newEndDate.setDate(newEndDate.getDate() - 1);

      let newStartDate = new Date(newEndDate.getTime());
      newStartDate.setDate(newEndDate.getDate() - 29);

      do {
        let theParameters = {
          botODM: botpressBotODM,
          analyticsStartDate: newStartDate,
          analyticsEndDate: newEndDate,
        };

        let theReturn = yield call(BotpressAPI.getBotAnalytics, theParameters);
        newAnalytics.unshift(...theReturn.records);
        // newAnalytics.push(...theReturn.records);

        newEndDate.setDate(newStartDate.getDate() - 1);
        newStartDate.setDate(newEndDate.getDate() - 29);
      } while (startDate <= newStartDate);

      yield put(
        Actions.loadAnalyticsSuccess({
          analytics: newAnalytics,
          analyticsStartDate: startDate,
        }),
      );
    } else {
      //data is already there, they asked for less than we have
      yield put(
        Actions.loadAnalyticsSuccess({
          analytics: currentAnalytics,
          analyticsStartDate: currentStartDate,
        }),
      );
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* loadBot({ payload }) {
  try {
    //All this shit is to handler initialization, you can pass in empty payloads after setting the bot
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (botpressBotODM != null) {
      let theParameters = {
        botODM: botpressBotODM,
      };
      let theReturn = yield call(BotpressAPI.getBot, theParameters);
      yield put(Actions.loadBotSuccess(theReturn.bot));
    } else {
      yield put(Actions.loadBotSuccess(null));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* loadBotpressSummary({ payload }) {
  try {
    //All this shit is to handler initialization, you can pass in empty payloads after setting the bot
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (botpressBotODM != null) {
      let theParameters = {
        botODM: botpressBotODM,
      };
      let theReturn = yield call(BotpressAPI.getBotpressSummary, theParameters);
      yield put(Actions.loadBotpressSummarySuccess(theReturn));
    } else {
      yield put(Actions.loadBotpressSummarySuccess(null));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

function* searchChatsWithConfig({
  payload,
  defaultBotSelector,
  paginationSelector,
  pagesSelector,
  startDateSelector,
  endDateSelector,
  successAction,
  analyze,
}) {
  const botpressBotODM = payload.botODM != null ? payload.botODM : yield select(defaultBotSelector);

  if (botpressBotODM != null) {
    const currentPagination = yield select(paginationSelector);
    let currentCacheInvalid = false;
    if (
      payload.pageSize !== currentPagination.pageSize ||
      JSON.stringify(payload.query) !== JSON.stringify(currentPagination.query) ||
      JSON.stringify(payload.sort) !== JSON.stringify(currentPagination.sort) ||
      payload.botODM != null ||
      payload.invalidateCache ||
      payload.hasOwnProperty("chatStartDate")
    ) {
      currentCacheInvalid = true;
    }
    let chatPages = yield select(pagesSelector);
    if (!currentCacheInvalid && chatPages.hasOwnProperty(payload.pageIndex)) {
      payload["botODM"] = botpressBotODM;
      const propertyCount = Object.keys(chatPages).length;
      const lastPageLength = chatPages[propertyCount - 1].odms.length;
      payload.totalItems = (propertyCount - 1) * payload.pageSize + lastPageLength;
      if (lastPageLength === payload.pageSize) {
        payload.totalItems += 1;
      }
      yield put(
        successAction({
          chatPages: chatPages,
          chats: chatPages[payload.pageIndex].odms,
          chatPagination: payload,
        }),
      );
    } else {
      let newChatPages = {};
      let tmpPayload = deepCopyObject(payload);
      tmpPayload["botODM"] = botpressBotODM;

      let startDate = yield select(startDateSelector);
      if (tmpPayload.hasOwnProperty("chatStartDate")) {
        startDate = tmpPayload.chatStartDate;
        delete tmpPayload.chatStartDate;
      }

      let endDate = yield select(endDateSelector);
      if (tmpPayload.hasOwnProperty("chatEndDate")) {
        endDate = tmpPayload.chatEndDate;
        delete tmpPayload.chatEndDate;
      }

      let query = tmpPayload.query;
      query["createdAt"] = {
        $gte: new Date(startDate),
        $lte: new Date(endDate),
      };

      tmpPayload.query = query;
      let hasMore = true;
      let index = 0;
      for (; hasMore; index++) {
        tmpPayload.pageIndex = index;
        let theReturn = yield call(BotpressAPI.searchConversations, tmpPayload);
        newChatPages[index] = { odms: theReturn.rows };
        hasMore = theReturn.hasMore;
      }

      --index;
      payload.totalItems = index * payload.pageSize + newChatPages[index].odms.length;

      yield put(
        successAction({
          chatPages: newChatPages,
          chats: newChatPages[payload.pageIndex]?.odms || [],
          chatPagination: payload,
        }),
      );

      if (analyze) {
        yield put(Actions.analyzeChatsRequest(newChatPages));
      }
    }
  } else {
    yield put(
      successAction({
        chatPages: {},
        chats: [],
        chatPagination: payload,
      }),
    );
  }
}

export function* searchChats({ payload }) {
  try {
    yield* searchChatsWithConfig({
      payload,
      defaultBotSelector: Actions.botpressBotODMSelector,
      paginationSelector: Actions.chatPaginationSelector,
      pagesSelector: Actions.chatPagesSelector,
      startDateSelector: Actions.chatStartDateSelector,
      endDateSelector: Actions.chatEndDatesSelector,
      successAction: Actions.searchChatsSuccess,
      analyze: true,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* searchTestChats({ payload }) {
  try {
    yield* searchChatsWithConfig({
      payload,
      defaultBotSelector: Actions.testBotODMSelector,
      paginationSelector: Actions.testChatPaginationSelector,
      pagesSelector: Actions.testChatPagesSelector,
      startDateSelector: Actions.testChatStartDateSelector,
      endDateSelector: Actions.testChatEndDatesSelector,
      successAction: Actions.searchTestChatsSuccess,
      analyze: false,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* searchFlaggedChats({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (botpressBotODM != null) {
      const currentPagination = yield select(Actions.flaggedChatPaginationSelector);
      //payload is a "currentPagination" from helpers.js
      let currentCacheInvalid = false;
      if (
        payload.pageSize !== currentPagination.pageSize ||
        JSON.stringify(payload.query) !== JSON.stringify(currentPagination.query) ||
        JSON.stringify(payload.sort) !== JSON.stringify(currentPagination.sort) ||
        payload.botODM != null ||
        payload.invalidateCache
      ) {
        //something fundamental has changed
        currentCacheInvalid = true;
      }
      let flaggedChatPages = yield select(Actions.flaggedChatPagesSelector);
      if (!currentCacheInvalid && flaggedChatPages.hasOwnProperty(payload.pageIndex)) {
        //FML
        console.log("BotpressSession: Already Have Row:  " + payload.pageIndex);

        payload["botODM"] = botpressBotODM;
        const propertyCount = Object.keys(flaggedChatPages).length;
        const lastPageLength = flaggedChatPages[propertyCount - 1].odms.length;
        payload.totalItems = (propertyCount - 1) * payload.pageSize + lastPageLength;
        if (lastPageLength === payload.pageSize) {
          payload.totalItems += 1; //to indicate more items
        }
        yield put(
          Actions.searchFlaggedChatsSuccess({
            flaggedChatPages: flaggedChatPages,
            flaggedChats: flaggedChatPages[payload.pageIndex].odms,
            flaggedChatPagination: payload,
          }),
        );
      } else {
        let newflaggedChatPages = {};
        let tmpPayload = deepCopyObject(payload);
        tmpPayload["botODM"] = botpressBotODM;

        let query = tmpPayload.query;
        query["tracking"] = true;

        tmpPayload.query = query;
        let hasMore = true;
        let index = 0;
        for (; hasMore; index++) {
          tmpPayload.pageIndex = index;
          let theReturn = yield call(BotpressAPI.searchConversations, tmpPayload);
          //upate the currentPagination
          newflaggedChatPages[index] = { odms: theReturn.rows };
          hasMore = theReturn.hasMore;
        }

        --index;
        payload.totalItems = index * payload.pageSize + newflaggedChatPages[index].odms.length;

        yield put(
          Actions.searchFlaggedChatsSuccess({
            flaggedChatPages: newflaggedChatPages,
            flaggedChats: newflaggedChatPages[payload.pageIndex]?.odms || [],
            flaggedChatPagination: payload,
          }),
        );
      }
    } else {
      yield put(
        Actions.searchFlaggedChatsSuccess({
          chatPages: {},
          chats: [],
          chatPagination: payload,
        }),
      );
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* setSearchChatsDates({ payload }) {
  try {
    //get the current and clear it out

    const currentPagination = yield select(Actions.chatPaginationSelector);

    currentPagination["chatStartDate"] = payload.chatStartDate;
    currentPagination["chatEndDate"] = payload.chatEndDate;
    yield put(Actions.setSearchChatsDatesSuccess(payload));

    yield put(Actions.searchChatsRequest(currentPagination));
  } catch (error) {
    yield put(Actions.loadAnalyticsRequest(null));
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* setSearchTestChatsDates({ payload }) {
  try {
    const currentPagination = yield select(Actions.testChatPaginationSelector);

    currentPagination["chatStartDate"] = payload.chatStartDate;
    currentPagination["chatEndDate"] = payload.chatEndDate;
    yield put(Actions.setSearchTestChatsDatesSuccess(payload));

    yield put(Actions.searchTestChatsRequest(currentPagination));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* analyzeChats({ payload }) {
  try {
    // const chatPages = payload != null ? payload : yield select(Actions.chatPagesSelector);
    const chatPages = payload;
    const pageCount = Object.keys(chatPages).length;

    //TODO standardize this..
    let theResults = {
      topicFrequency: [],
    };

    const frequencyMap = new Map();
    const sentimentMap = new Map();

    for (let pageIndex = 0; pageIndex < pageCount; ++pageIndex) {
      for (let rowIndex = 0; rowIndex < chatPages[pageIndex].odms.length; ++rowIndex) {
        let currentRow = chatPages[pageIndex].odms[rowIndex];
        if (currentRow.topics != null) {
          for (let topicIndex = 0; topicIndex < currentRow.topics.length; ++topicIndex) {
            let theTopic = currentRow.topics[topicIndex];
            frequencyMap.set(theTopic, (frequencyMap.get(theTopic) || 0) + 1);
          }
        }
        if (currentRow.sentiment != null) {
          sentimentMap.set(currentRow.sentiment, (sentimentMap.get(currentRow.sentiment) || 0) + 1);
        }
      }
    }

    const sortedMapDesc = new Map([...frequencyMap].sort((a, b) => b[1] - a[1]));
    theResults["topicFrequency"] = sortedMapDesc;

    const sortedSentimentMap = new Map([...sentimentMap].sort((a, b) => b[1] - a[1]));
    theResults["sentimentFrequency"] = sortedSentimentMap;

    yield put(Actions.analyzeChatsSuccess(theResults));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

function* deleteChatWithConfig({
  payload,
  defaultBotSelector,
  paginationSelector,
  reloadAction,
  successAction,
}) {
  const botpressBotODM = payload.botODM != null ? payload.botODM : yield select(defaultBotSelector);

  if (payload.conversationId != null) {
    payload["botODM"] = botpressBotODM;
    let theReturn = yield call(BotpressAPI.deleteConversation, payload);

    if (Object.hasOwn(theReturn, "code")) {
      yield put(Actions.botpressSessionRequestFailed(theReturn));
    } else {
      const chatPage = yield select(paginationSelector);
      chatPage["botODM"] = botpressBotODM;
      chatPage["invalidateCache"] = true;
      yield put(reloadAction(chatPage));
    }
  }
  yield put(successAction({}));
}

export function* deleteChat({ payload }) {
  try {
    yield* deleteChatWithConfig({
      payload,
      defaultBotSelector: Actions.botpressBotODMSelector,
      paginationSelector: Actions.chatPaginationSelector,
      reloadAction: Actions.searchChatsRequest,
      successAction: Actions.deleteChatSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* deleteTestChat({ payload }) {
  try {
    yield* deleteChatWithConfig({
      payload,
      defaultBotSelector: Actions.testBotODMSelector,
      paginationSelector: Actions.testChatPaginationSelector,
      reloadAction: Actions.searchTestChatsRequest,
      successAction: Actions.deleteTestChatSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

function* updateChatWithConfig({
  payload,
  defaultBotSelector,
  paginationSelector,
  reloadAction,
  successAction,
  reloadFlagged,
}) {
  const botpressBotODM = payload.botODM != null ? payload.botODM : yield select(defaultBotSelector);

  if (payload.chatRecords != null) {
    payload["botODM"] = botpressBotODM;
    let theReturn = yield call(BotpressAPI.updateChat, payload);

    if (Object.hasOwn(theReturn, "code")) {
      yield put(Actions.botpressSessionRequestFailed(theReturn));
    } else {
      const chatPage = yield select(paginationSelector);
      chatPage["botODM"] = botpressBotODM;
      chatPage["invalidateCache"] = true;
      yield put(reloadAction(chatPage));

      if (reloadFlagged) {
        const flaggedChatPage = yield select(Actions.flaggedChatPaginationSelector);
        flaggedChatPage["botODM"] = botpressBotODM;
        flaggedChatPage["invalidateCache"] = true;

        yield put(Actions.searchFlaggedChatsRequest(flaggedChatPage));
      }
    }
    yield put(successAction({}));
  }
}

export function* updateChat({ payload }) {
  try {
    yield* updateChatWithConfig({
      payload,
      defaultBotSelector: Actions.botpressBotODMSelector,
      paginationSelector: Actions.chatPaginationSelector,
      reloadAction: Actions.searchChatsRequest,
      successAction: Actions.updateChatSuccess,
      reloadFlagged: true,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* updateTestChat({ payload }) {
  try {
    yield* updateChatWithConfig({
      payload,
      defaultBotSelector: Actions.testBotODMSelector,
      paginationSelector: Actions.testChatPaginationSelector,
      reloadAction: Actions.searchTestChatsRequest,
      successAction: Actions.updateTestChatSuccess,
      reloadFlagged: false,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

// Shared feedback search used by both the PROD ("Production Feedback") and TEST
// ("Test Feedback") widgets. The two differ only in which bot the feedback is
// read from and which redux slice / success action it lands in, so those are
// passed in via config while the paging/cache logic stays in one place.
function* searchFeedbackWithConfig({
  payload,
  defaultBotSelector,
  paginationSelector,
  pagesSelector,
  successAction,
}) {
  //payload is a "pagination" from helpers.js
  const botpressBotODM = payload.botODM != null ? payload.botODM : yield select(defaultBotSelector);

  const currentPagination = yield select(paginationSelector);
  if (botpressBotODM != null) {
    //payload is a "currentPagination" from helpers.js
    let currentCacheInvalid = false;
    if (
      payload.pageSize !== currentPagination.pageSize ||
      JSON.stringify(payload.query) !== JSON.stringify(currentPagination.query) ||
      JSON.stringify(payload.sort) !== JSON.stringify(currentPagination.sort) ||
      payload.botODM != null ||
      payload.invalidateCache
    ) {
      //something fundamental has changed
      currentCacheInvalid = true;
    }
    let feedbackPages = yield select(pagesSelector);
    if (!currentCacheInvalid && feedbackPages.hasOwnProperty(payload.pageIndex)) {
      //FML
      payload["botODM"] = botpressBotODM;
      const propertyCount = Object.keys(feedbackPages).length;
      const lastPageLength = feedbackPages[propertyCount - 1].odms.length;
      payload.totalItems = (propertyCount - 1) * payload.pageSize + lastPageLength;
      if (lastPageLength === payload.pageSize) {
        payload.totalItems += 1; //to indicate more items
      }
      yield put(
        successAction({
          feedbackPages: feedbackPages,
          feedback: feedbackPages[payload.pageIndex].odms,
          feedbackPagination: payload,
        }),
      );
    } else {
      let newFeedbackPages = {};
      let tmpPayload = deepCopyObject(payload);
      tmpPayload["botODM"] = botpressBotODM;

      let hasMore = true;
      let index = 0;
      for (; hasMore; index++) {
        tmpPayload.pageIndex = index;
        let theReturn = yield call(BotpressAPI.searchFeedback, tmpPayload);
        //upate the currentPagination
        newFeedbackPages[index] = { odms: theReturn.rows };
        hasMore = theReturn.hasMore;
      }

      --index;
      payload.totalItems = index * payload.pageSize + newFeedbackPages[index].odms.length;

      yield put(
        successAction({
          feedbackPages: newFeedbackPages,
          feedback: newFeedbackPages[payload.pageIndex]?.odms || [],
          feedbackPagination: payload,
        }),
      );
    }
  } else {
    yield put(
      successAction({
        feedbackPages: {},
        feedback: [],
        feedbackPagination: payload,
      }),
    );
  }
}

export function* searchFeedback({ payload }) {
  try {
    yield* searchFeedbackWithConfig({
      payload,
      defaultBotSelector: Actions.botpressBotODMSelector,
      paginationSelector: Actions.feedbackPaginationSelector,
      pagesSelector: Actions.feedbackPagesSelector,
      successAction: Actions.searchFeedbackSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* searchTestFeedback({ payload }) {
  try {
    yield* searchFeedbackWithConfig({
      payload,
      defaultBotSelector: Actions.testBotODMSelector,
      paginationSelector: Actions.testFeedbackPaginationSelector,
      pagesSelector: Actions.testFeedbackPagesSelector,
      successAction: Actions.searchTestFeedbackSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

// Shared feedback update used by both the PROD and TEST feedback widgets. The
// reload after a successful write goes back to the same slice/bot the write
// targeted, so the TEST widget refreshes TEST feedback and the PROD widget
// refreshes PROD feedback.
function* updateFeedbackWithConfig({
  payload,
  defaultBotSelector,
  paginationSelector,
  reloadAction,
  successAction,
}) {
  //payload is a "pagination" from helpers.js
  const botpressBotODM = payload.botODM != null ? payload.botODM : yield select(defaultBotSelector);

  if (payload.feedbackRecords != null) {
    let results = [];

    //delete the chat
    payload["botODM"] = botpressBotODM;
    let theReturn = null;
    if (payload.feedbackRecords.length === 1 && payload.feedbackRecords[0].id == null) {
      theReturn = yield call(BotpressAPI.addFeedback, payload);
      results = theReturn.rows;
    } else {
      theReturn = yield call(BotpressAPI.updateFeedback, payload);
      results = theReturn.inserted.concat(theReturn.updated);
    }

    if (Object.hasOwn(theReturn, "code")) {
      yield put(Actions.botpressSessionRequestFailed(theReturn));
    } else if (!payload.skipFeedbackReload) {
      // skipFeedbackReload lets callers persist feedback without reloading the
      // feedback table.
      const feedbackPage = yield select(paginationSelector);
      //setting the botODM triggers reload
      feedbackPage["botODM"] = botpressBotODM;
      feedbackPage["invalidateCache"] = true;
      yield put(reloadAction(feedbackPage));
    }

    yield put(successAction({}));

    //check for the conversation feedback
    const conversationId = yield select(Actions.conversationIdSelector);
    if (conversationId != null && results.length > 0) {
      //this only works for one at a time
      let currentList = yield select(Actions.conversationFeedbackSelector);
      let doIt = false;
      for (let i = 0; i < results.length; ++i) {
        if (results[i].conversationId === conversationId) {
          currentList =
            currentList == null ? [] : currentList.filter(obj => obj.id !== results[i].id);
          currentList.push(results[i]);
          doIt = true;
        }
      }

      if (doIt) {
        yield put(Actions.updateConversationFeedbackSuccess(currentList));
      }
    }
  }
}

export function* updateFeedback({ payload }) {
  try {
    yield* updateFeedbackWithConfig({
      payload,
      defaultBotSelector: Actions.botpressBotODMSelector,
      paginationSelector: Actions.feedbackPaginationSelector,
      reloadAction: Actions.searchFeedbackRequest,
      successAction: Actions.updateFeedbackSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* updateTestFeedback({ payload }) {
  try {
    yield* updateFeedbackWithConfig({
      payload,
      defaultBotSelector: Actions.testBotODMSelector,
      paginationSelector: Actions.testFeedbackPaginationSelector,
      reloadAction: Actions.searchTestFeedbackRequest,
      successAction: Actions.updateTestFeedbackSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

// Shared feedback delete used by both the PROD and TEST feedback widgets.
function* deleteFeedbackWithConfig({
  payload,
  defaultBotSelector,
  paginationSelector,
  reloadAction,
  successAction,
}) {
  //payload is a "pagination" from helpers.js
  const botpressBotODM = payload.botODM != null ? payload.botODM : yield select(defaultBotSelector);

  if (payload.feedbackIDs != null) {
    //delete the chat
    payload["botODM"] = botpressBotODM;
    let theReturn = yield call(BotpressAPI.deleteFeedback, payload);
    if (Object.hasOwn(theReturn, "code")) {
      yield put(Actions.botpressSessionRequestFailed(theReturn));
    } else if (!payload.skipFeedbackReload) {
      // skipFeedbackReload lets callers persist feedback without reloading the
      // feedback table.
      const feedbackPage = yield select(paginationSelector);
      //setting the botODM triggers reload
      feedbackPage["botODM"] = botpressBotODM;
      yield put(reloadAction(feedbackPage));
    }
  }
  yield put(successAction({}));
  const conversationId = yield select(Actions.conversationIdSelector);
  if (conversationId != null) {
    //this only works for one at a time
    let currentList = yield select(Actions.conversationFeedbackSelector);
    for (let i = 0; i < payload.feedbackIDs.length; ++i) {
      currentList =
        currentList == null ? [] : currentList.filter(obj => obj.id !== payload.feedbackIDs[i]);
    }
    yield put(Actions.updateConversationFeedbackSuccess(currentList));
  }
}

export function* deleteFeedback({ payload }) {
  try {
    yield* deleteFeedbackWithConfig({
      payload,
      defaultBotSelector: Actions.botpressBotODMSelector,
      paginationSelector: Actions.feedbackPaginationSelector,
      reloadAction: Actions.searchFeedbackRequest,
      successAction: Actions.deleteFeedbackSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* deleteTestFeedback({ payload }) {
  try {
    yield* deleteFeedbackWithConfig({
      payload,
      defaultBotSelector: Actions.testBotODMSelector,
      paginationSelector: Actions.testFeedbackPaginationSelector,
      reloadAction: Actions.searchTestFeedbackRequest,
      successAction: Actions.deleteTestFeedbackSuccess,
    });
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

//#region Assessment

export function* searchAssessment({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const testBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.testBotODMSelector);

    const currentPagination = yield select(Actions.assessmentPaginationSelector);
    if (testBotODM != null) {
      //payload is a "currentPagination" from helpers.js
      let currentCacheInvalid = false;
      if (
        payload.pageSize !== currentPagination.pageSize ||
        JSON.stringify(payload.query) !== JSON.stringify(currentPagination.query) ||
        JSON.stringify(payload.sort) !== JSON.stringify(currentPagination.sort) ||
        payload.botODM != null ||
        payload.invalidateCache
      ) {
        //something fundamental has changed
        currentCacheInvalid = true;
      }
      let assessmentPages = yield select(Actions.assessmentPagesSelector);
      if (!currentCacheInvalid && assessmentPages.hasOwnProperty(payload.pageIndex)) {
        //FML
        payload["botODM"] = testBotODM;
        const propertyCount = Object.keys(assessmentPages).length;
        const lastPageLength = assessmentPages[propertyCount - 1].odms.length;
        payload.totalItems = (propertyCount - 1) * payload.pageSize + lastPageLength;
        if (lastPageLength === payload.pageSize) {
          payload.totalItems += 1; //to indicate more items
        }
        yield put(
          Actions.searchAssessmentSuccess({
            assessmentPages: assessmentPages,
            assessment: assessmentPages[payload.pageIndex].odms,
            assessmentPagination: payload,
          }),
        );
      } else {
        payload["botODM"] = testBotODM;
        let theReturn = yield call(BotpressAPI.searchAssessment, payload);
        //upate the currentPagination
        payload.totalItems = payload.pageIndex * payload.pageSize + theReturn.rows.length;
        if (theReturn.hasMore) {
          //assumes payload is full fo rthe pageSize if true
          payload.totalItems += 1; //to indicate more items
        }

        if (currentCacheInvalid) {
          assessmentPages = {};
        }
        assessmentPages[payload.pageIndex] = { odms: theReturn.rows };

        yield put(
          Actions.searchAssessmentSuccess({
            assessmentPages: assessmentPages,
            assessment: theReturn.rows,
            assessmentPagination: payload,
          }),
        );
      }
    } else {
      yield put(
        Actions.searchAssessmentSuccess({
          assessmentPages: {},
          assessment: [],
          assessmentPagination: payload,
        }),
      );
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* updateAssessment({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const testBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.testBotODMSelector);

    if (payload.assessmentRecord != null) {
      // Status before this save, from the loaded page, so the feedback sync
      // only fires on an actual status change.
      const loadedAssessments = yield select(state => state.botpressSession.assessment);
      const previousStatus = (loadedAssessments || []).find(
        row => row.id != null && row.id === payload.assessmentRecord.id,
      )?.status;

      //delete the chat
      payload["botODM"] = testBotODM;
      let theReturn = null;
      if (payload.assessmentRecord.id == null) {
        theReturn = yield call(BotpressAPI.addAssessment, payload);
      } else {
        theReturn = yield call(BotpressAPI.updateAssessment, payload);
      }

      if (Object.hasOwn(theReturn, "code")) {
        yield put(Actions.botpressSessionRequestFailed(theReturn));
      } else {
        yield* syncFeedbackFromAssessment(payload.assessmentRecord, previousStatus);

        const assessmentPage = yield select(Actions.assessmentPaginationSelector);
        //setting the botODM triggers reload
        assessmentPage["botODM"] = testBotODM;
        yield put(Actions.searchAssessmentRequest(assessmentPage));
      }
    }

    //reload
    Actions.updateAssessmentSuccess({});
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* deleteAssessments({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const testBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.testBotODMSelector);

    if (payload.assessmentIDs != null) {
      //delete the chat
      payload["botODM"] = testBotODM;
      let theReturn = yield call(BotpressAPI.deleteAssessments, payload);
      if (Object.hasOwn(theReturn, "code")) {
        yield put(Actions.botpressSessionRequestFailed(theReturn));
      } else {
        const assessmentPage = yield select(Actions.assessmentPaginationSelector);
        //setting the botODM triggers reload
        assessmentPage["botODM"] = testBotODM;
        yield put(Actions.searchAssessmentRequest(assessmentPage));
      }
    }

    //reload
    Actions.deleteAssessmentsSuccess({});
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

//#endregion

//#region Products

export function* searchProducts({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    const currentPagination = yield select(Actions.productPaginationSelector);

    if (botpressBotODM != null) {
      //payload is a "currentPagination" from helpers.js
      let currentCacheInvalid = false;
      if (
        payload.pageSize !== currentPagination.pageSize ||
        JSON.stringify(payload.query) !== JSON.stringify(currentPagination.query) ||
        JSON.stringify(payload.sort) !== JSON.stringify(currentPagination.sort) ||
        payload.botODM != null ||
        payload.invalidateCache
      ) {
        //something fundamental has changed
        currentCacheInvalid = true;
      }

      let productPages = yield select(Actions.productPagesSelector);

      if (!currentCacheInvalid && productPages.hasOwnProperty(payload.pageIndex)) {
        //FML
        payload["botODM"] = botpressBotODM;

        const propertyCount = Object.keys(productPages).length;
        const lastPageLength = productPages[propertyCount - 1].odms.length;
        payload.totalItems = (propertyCount - 1) * payload.pageSize + lastPageLength;

        if (lastPageLength === payload.pageSize) {
          payload.totalItems += 1; //to indicate more items
        }

        const allProducts = Object.keys(productPages)
          .sort((a, b) => Number(a) - Number(b))
          .flatMap(key => productPages[key].odms);

        yield put(
          Actions.searchProductsSuccess({
            productPages: productPages,
            products: allProducts,
            productPagination: payload,
          }),
        );
      } else {
        let newProductPages = {};
        let tmpPayload = deepCopyObject(payload);
        tmpPayload["botODM"] = botpressBotODM;

        let hasMore = true;
        let index = 0;
        let allProducts = [];

        for (; hasMore; index++) {
          tmpPayload.pageIndex = index;

          let theReturn = yield call(BotpressAPI.searchProducts, tmpPayload);

          //upate the currentPagination
          if (Object.hasOwn(theReturn, "code")) {
            newProductPages = {};
            allProducts = [];
            hasMore = false;
          } else {
            newProductPages[index] = { odms: theReturn.rows };
            allProducts = allProducts.concat(theReturn.rows);
            hasMore = theReturn.hasMore;
          }
        }

        --index;
        payload.totalItems = allProducts.length;

        yield put(
          Actions.searchProductsSuccess({
            productPages: newProductPages,
            products: allProducts,
            productPagination: payload,
          }),
        );
      }
    } else {
      yield put(
        Actions.searchProductsSuccess({
          productPages: {},
          products: [],
          productPagination: payload,
        }),
      );
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* updateProduct({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (payload.productRecord != null) {
      //delete the chat
      payload["botODM"] = botpressBotODM;
      let theReturn = null;

      if (payload.productRecord.id == null) {
        theReturn = yield call(BotpressAPI.addProduct, payload);
      } else {
        theReturn = yield call(BotpressAPI.updateProduct, payload);
      }

      if (Object.hasOwn(theReturn, "code")) {
        yield put(Actions.botpressSessionRequestFailed(theReturn));
      } else {
        const productPage = yield select(Actions.productPaginationSelector);
        //setting the botODM triggers reload
        productPage["botODM"] = botpressBotODM;
        productPage["invalidateCache"] = true;
        yield put(Actions.searchProductsRequest(productPage));
      }
    }

    //reload
    yield put(Actions.updateProductSuccess({}));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* deleteProducts({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (payload.productIDs != null) {
      //delete the chat
      payload["botODM"] = botpressBotODM;
      let theReturn = yield call(BotpressAPI.deleteProducts, payload);

      if (Object.hasOwn(theReturn, "code")) {
        yield put(Actions.botpressSessionRequestFailed(theReturn));
      } else {
        const productPage = yield select(Actions.productPaginationSelector);
        //setting the botODM triggers reload
        productPage["botODM"] = botpressBotODM;
        productPage["invalidateCache"] = true;
        yield put(Actions.searchProductsRequest(productPage));
      }
    }

    //reload
    yield put(Actions.deleteProductsSuccess({}));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

//#endregion

//#region Members

export function* searchMembers({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    const currentPagination = yield select(Actions.memberPaginationSelector);

    if (botpressBotODM != null) {
      //payload is a "currentPagination" from helpers.js
      let currentCacheInvalid = false;
      if (
        payload.pageSize !== currentPagination.pageSize ||
        JSON.stringify(payload.query) !== JSON.stringify(currentPagination.query) ||
        JSON.stringify(payload.sort) !== JSON.stringify(currentPagination.sort) ||
        payload.botODM != null ||
        payload.invalidateCache
      ) {
        //something fundamental has changed
        currentCacheInvalid = true;
      }

      let memberPages = yield select(Actions.memberPagesSelector);

      if (!currentCacheInvalid && memberPages.hasOwnProperty(payload.pageIndex)) {
        //FML
        payload["botODM"] = botpressBotODM;

        const propertyCount = Object.keys(memberPages).length;
        const lastPageLength = memberPages[propertyCount - 1].odms.length;
        payload.totalItems = (propertyCount - 1) * payload.pageSize + lastPageLength;

        if (lastPageLength === payload.pageSize) {
          payload.totalItems += 1; //to indicate more items
        }

        const allMembers = Object.keys(memberPages)
          .sort((a, b) => Number(a) - Number(b))
          .flatMap(key => memberPages[key].odms);

        yield put(
          Actions.searchMembersSuccess({
            memberPages: memberPages,
            members: allMembers,
            memberPagination: payload,
          }),
        );
      } else {
        let newMemberPages = {};
        let tmpPayload = deepCopyObject(payload);
        tmpPayload["botODM"] = botpressBotODM;

        let hasMore = true;
        let index = 0;
        let allMembers = [];

        for (; hasMore; index++) {
          tmpPayload.pageIndex = index;

          let theReturn = yield call(BotpressAPI.searchMembers, tmpPayload);

          //upate the currentPagination
          if (Object.hasOwn(theReturn, "code")) {
            newMemberPages = {};
            allMembers = [];
            hasMore = false;
          } else {
            newMemberPages[index] = { odms: theReturn.rows };
            allMembers = allMembers.concat(theReturn.rows);
            hasMore = theReturn.hasMore;
          }
        }

        --index;
        payload.totalItems = allMembers.length;

        yield put(
          Actions.searchMembersSuccess({
            memberPages: newMemberPages,
            members: allMembers,
            memberPagination: payload,
          }),
        );
      }
    } else {
      yield put(
        Actions.searchMembersSuccess({
          memberPages: {},
          members: [],
          memberPagination: payload,
        }),
      );
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

//#endregion

//#region Presented Links

export function* searchPresentedLinks({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    const currentPagination = yield select(Actions.presentedLinkPaginationSelector);

    if (botpressBotODM != null) {
      //payload is a "currentPagination" from helpers.js
      let currentCacheInvalid = false;
      if (
        payload.pageSize !== currentPagination.pageSize ||
        JSON.stringify(payload.query) !== JSON.stringify(currentPagination.query) ||
        JSON.stringify(payload.sort) !== JSON.stringify(currentPagination.sort) ||
        payload.botODM != null ||
        payload.invalidateCache
      ) {
        //something fundamental has changed
        currentCacheInvalid = true;
      }

      let presentedLinkPages = yield select(Actions.presentedLinkPagesSelector);

      if (!currentCacheInvalid && presentedLinkPages.hasOwnProperty(payload.pageIndex)) {
        //FML
        payload["botODM"] = botpressBotODM;

        const propertyCount = Object.keys(presentedLinkPages).length;
        const lastPageLength = presentedLinkPages[propertyCount - 1].odms.length;
        payload.totalItems = (propertyCount - 1) * payload.pageSize + lastPageLength;

        if (lastPageLength === payload.pageSize) {
          payload.totalItems += 1; //to indicate more items
        }

        const allPresentedLinks = Object.keys(presentedLinkPages)
          .sort((a, b) => Number(a) - Number(b))
          .flatMap(key => presentedLinkPages[key].odms);

        yield put(
          Actions.searchPresentedLinksSuccess({
            presentedLinkPages: presentedLinkPages,
            presentedLinks: allPresentedLinks,
            presentedLinkPagination: payload,
          }),
        );
      } else {
        let newPresentedLinkPages = {};
        let tmpPayload = deepCopyObject(payload);
        tmpPayload["botODM"] = botpressBotODM;

        let hasMore = true;
        let index = 0;
        let allPresentedLinks = [];

        for (; hasMore; index++) {
          tmpPayload.pageIndex = index;

          let theReturn = yield call(BotpressAPI.searchPresentedLinks, tmpPayload);

          //upate the currentPagination
          if (Object.hasOwn(theReturn, "code")) {
            newPresentedLinkPages = {};
            allPresentedLinks = [];
            hasMore = false;
          } else {
            newPresentedLinkPages[index] = { odms: theReturn.rows };
            allPresentedLinks = allPresentedLinks.concat(theReturn.rows);
            hasMore = theReturn.hasMore;
          }
        }

        --index;
        payload.totalItems = allPresentedLinks.length;

        yield put(
          Actions.searchPresentedLinksSuccess({
            presentedLinkPages: newPresentedLinkPages,
            presentedLinks: allPresentedLinks,
            presentedLinkPagination: payload,
          }),
        );
      }
    } else {
      yield put(
        Actions.searchPresentedLinksSuccess({
          presentedLinkPages: {},
          presentedLinks: [],
          presentedLinkPagination: payload,
        }),
      );
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

//#endregion

//#region Knowledge Base URLs

export function* loadKnowledgeBaseUrls({ payload }) {
  try {
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (botpressBotODM != null) {
      const theReturn = yield call(BotpressAPI.getBotpressScrapedWebsiteUrls, {
        botODM: botpressBotODM,
      });

      yield put(Actions.loadKnowledgeBaseUrlsSuccess(theReturn || []));
    } else {
      yield put(Actions.loadKnowledgeBaseUrlsSuccess([]));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
    yield put(Actions.loadKnowledgeBaseUrlsSuccess([]));
  }
}

export function* loadKnowledgeBaseMarkdownFiles({ payload }) {
  try {
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (botpressBotODM != null) {
      const theReturn = yield call(BotpressAPI.getBotpressMarkdownFiles, {
        botODM: botpressBotODM,
      });

      yield put(Actions.loadKnowledgeBaseMarkdownFilesSuccess(theReturn || []));
    } else {
      yield put(Actions.loadKnowledgeBaseMarkdownFilesSuccess([]));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
    yield put(Actions.loadKnowledgeBaseMarkdownFilesSuccess([]));
  }
}

export function* updateKnowledgeBaseMarkdownFile({ payload }) {
  try {
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);

    if (botpressBotODM != null) {
      const theReturn = yield call(BotpressAPI.upsertBotpressMarkdownFile, {
        ...payload,
        botODM: botpressBotODM,
      });

      if (Object.hasOwn(theReturn, "code")) {
        yield put(Actions.botpressSessionRequestFailed(theReturn));
      } else {
        // Botpress needs time to finish indexing the uploaded markdown file.
        // If we reload immediately, it can temporarily return 0 rows.
        yield delay(5000);

        yield put(
          Actions.loadKnowledgeBaseMarkdownFilesRequest({
            botODM: botpressBotODM,
          }),
        );
      }
    }

    yield put(Actions.updateKnowledgeBaseMarkdownFileSuccess({}));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* loadTestKnowledgeBaseMarkdownFiles({ payload }) {
  try {
    const testBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.testBotODMSelector);

    if (testBotODM != null) {
      const theReturn = yield call(BotpressAPI.getBotpressMarkdownFiles, {
        botODM: testBotODM,
      });

      yield put(Actions.loadTestKnowledgeBaseMarkdownFilesSuccess(theReturn || []));
    } else {
      yield put(Actions.loadTestKnowledgeBaseMarkdownFilesSuccess([]));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
    yield put(Actions.loadTestKnowledgeBaseMarkdownFilesSuccess([]));
  }
}

export function* updateTestKnowledgeBaseMarkdownFile({ payload }) {
  try {
    const testBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.testBotODMSelector);

    if (testBotODM != null) {
      const theReturn = yield call(BotpressAPI.upsertBotpressMarkdownFile, {
        ...payload,
        botODM: testBotODM,
      });

      if (Object.hasOwn(theReturn, "code")) {
        yield put(Actions.botpressSessionRequestFailed(theReturn));
      } else {
        if (payload.deleteFileId != null) {
          yield call(BotpressAPI.deleteBotpressFile, {
            botODM: testBotODM,
            fileId: payload.deleteFileId,
          });
        }

        // Botpress needs time to finish indexing the uploaded markdown file.
        // If we reload immediately, it can temporarily return 0 rows.
        yield delay(5000);

        yield put(
          Actions.loadTestKnowledgeBaseMarkdownFilesRequest({
            botODM: testBotODM,
          }),
        );
      }
    }

    yield put(Actions.updateTestKnowledgeBaseMarkdownFileSuccess({}));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* loadTestKnowledgeBases({ payload }) {
  try {
    const testBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.testBotODMSelector);

    if (testBotODM != null) {
      const files = yield call(BotpressAPI.listAllBotpressFiles, {
        botODM: testBotODM,
        indexed: true,
      });

      const allFiles = files || [];

      const managedKbIds = new Set();
      const kbNamesById = new Map();

      for (const file of allFiles) {
        if ((file?.tags?.source || "") !== "knowledge-base") {
          continue;
        }

        const kbId = file?.tags?.kbId;

        if (!kbId) {
          continue;
        }

        if (file?.tags?.kbName) {
          kbNamesById.set(kbId, file.tags.kbName);
        }

        if (file?.tags?.kbName || file?.tags?.dsId || file?.tags?.dsType) {
          managedKbIds.add(kbId);
        }
      }

      yield put(
        Actions.loadTestKnowledgeBasesSuccess(
          summarizeKnowledgeBases(allFiles).map(kb => ({
            ...kb,
            kbName: kbNamesById.get(kb.kbId),
            isManaged: managedKbIds.has(kb.kbId),
          })),
        ),
      );
    } else {
      yield put(Actions.loadTestKnowledgeBasesSuccess([]));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
    yield put(Actions.loadTestKnowledgeBasesSuccess([]));
  }
}

//#endregion

export function* loadConversation({ payload }) {
  try {
    // payload is either the conversationId string, or an object
    // { conversationId, botODM } so callers can scope the load to a specific
    // bot (e.g. the TEST bot for assessment/test-harness conversations).
    const conversationId =
      payload != null && typeof payload === "object" ? payload.conversationId : payload;
    const botpressBotODM =
      payload && payload.botODM != null
        ? payload.botODM
        : yield select(Actions.botpressBotODMSelector);

    if (conversationId != null) {
      const credentialedPayload = { botODM: botpressBotODM, conversationId };
      const theMessages = yield call(BotpressAPI.loadConversation, credentialedPayload);
      //load feedback

      const newPage = createOmniPaginationObject(null, 50);
      newPage.pageIndex = 0;
      newPage.sort = { createdAt: "desc" };
      newPage.query = { conversationId };
      newPage["botODM"] = botpressBotODM;

      const feedbackRecords = yield call(BotpressAPI.searchFeedback, newPage);

      yield put(
        Actions.loadConversationSuccess({
          conversationId,
          conversation: theMessages,
          conversationFeedback: feedbackRecords.rows,
        }),
      );
    } else {
      //reset it, null payload
      yield put(
        Actions.loadConversationSuccess({
          conversationId: null,
          conversation: null,
          conversationFeedback: null,
        }),
      );
    }
    // //reload
    // Actions.loadConversationSuccess(payload);
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* setBotAssessmentState({ payload }) {
  try {
    let theParameters = {
      conversationId: payload.conversationId,
      userKey: payload.userKey,
    };

    yield put(Actions.setBotAssessmentStateSuccess(theParameters));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* loadConfigurations({ payload }) {
  try {
    //payload is a "pagination" from helpers.js
    const botpressBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.botpressBotODMSelector);
    if (botpressBotODM != null) {
      let tmpPayload = getDefaultPagination();
      tmpPayload["botODM"] = botpressBotODM;
      let hasMore = true;
      let index = 0;
      let newRows = [];
      for (; hasMore; index++) {
        tmpPayload.pageIndex = index;
        let theReturn = yield call(BotpressAPI.searchConfiguration, tmpPayload);
        newRows = newRows.concat(theReturn.rows);
        hasMore = theReturn.hasMore;
      }
      yield put(Actions.loadConfigurationsSuccess(newRows));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

// Mirror of loadConfigurations, but scoped to the TEST bot and stored in the
// separate testConfigurations slice. The TEST bot's version row is read from
// here when stamping new assessment records.
export function* loadTestConfigurations({ payload }) {
  try {
    const testBotODM =
      payload.botODM != null ? payload.botODM : yield select(Actions.testBotODMSelector);
    if (testBotODM != null) {
      let tmpPayload = getDefaultPagination();
      tmpPayload["botODM"] = testBotODM;
      let hasMore = true;
      let index = 0;
      let newRows = [];
      for (; hasMore; index++) {
        tmpPayload.pageIndex = index;
        let theReturn = yield call(BotpressAPI.searchConfiguration, tmpPayload);
        newRows = newRows.concat(theReturn.rows);
        hasMore = theReturn.hasMore;
      }
      yield put(Actions.loadTestConfigurationsSuccess(newRows));
    }
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

export function* setRerunConversationId({ payload }) {
  try {
    if (payload == null) {
      yield put(
        Actions.setRerunConversationIdSuccess({
          rerunConversationId: null,
          rerunConversationNumber: null,
          rerunFeedbackNumber: null,
        }),
      );
    }
    yield put(Actions.setRerunConversationIdSuccess(payload));
  } catch (error) {
    yield put(Actions.botpressSessionRequestFailed(error));
  }
}

//  saga
export default function* userSessionSaga() {
  yield takeLatest(Actions.setBotpressBotODMRequest, setBotpressBotODM);
  yield takeLatest(Actions.setAnalyticsBotODMRequest, setAnalyticsBotODM);
  yield takeLatest(Actions.setTestBotODMRequest, setTestBotODM);
  yield takeLatest(Actions.loadAnalyticsRequest, loadAnalytics);
  yield takeLatest(Actions.loadConfigurationsRequest, loadConfigurations);
  yield takeLatest(Actions.loadTestConfigurationsRequest, loadTestConfigurations);
  yield takeLatest(Actions.loadBotpressSummaryRequest, loadBotpressSummary);
  yield takeLatest(Actions.loadBotRequest, loadBot);
  yield takeLatest(Actions.searchChatsRequest, searchChats);
  yield takeLatest(Actions.setSearchChatsDatesRequest, setSearchChatsDates);
  yield takeLatest(Actions.analyzeChatsRequest, analyzeChats);
  yield takeLatest(Actions.deleteChatRequest, deleteChat);
  yield takeLatest(Actions.updateChatRequest, updateChat);
  yield takeLatest(Actions.searchTestChatsRequest, searchTestChats);
  yield takeLatest(Actions.setSearchTestChatsDatesRequest, setSearchTestChatsDates);
  yield takeLatest(Actions.deleteTestChatRequest, deleteTestChat);
  yield takeLatest(Actions.updateTestChatRequest, updateTestChat);
  yield takeLatest(Actions.searchFeedbackRequest, searchFeedback);
  yield takeLatest(Actions.updateFeedbackRequest, updateFeedback);
  yield takeLatest(Actions.deleteFeedbackRequest, deleteFeedback);
  yield takeLatest(Actions.searchTestFeedbackRequest, searchTestFeedback);
  yield takeLatest(Actions.updateTestFeedbackRequest, updateTestFeedback);
  yield takeLatest(Actions.deleteTestFeedbackRequest, deleteTestFeedback);
  yield takeLatest(Actions.searchFlaggedChatsRequest, searchFlaggedChats);
  yield takeLatest(Actions.searchAssessmentRequest, searchAssessment);
  yield takeLatest(Actions.updateAssessmentRequest, updateAssessment);
  yield takeLatest(Actions.deleteAssessmentsRequest, deleteAssessments);
  yield takeLatest(Actions.searchProductsRequest, searchProducts);
  yield takeLatest(Actions.updateProductRequest, updateProduct);
  yield takeLatest(Actions.deleteProductsRequest, deleteProducts);
  yield takeLatest(Actions.searchMembersRequest, searchMembers);
  yield takeLatest(Actions.searchPresentedLinksRequest, searchPresentedLinks);
  yield takeLatest(Actions.loadKnowledgeBaseUrlsRequest, loadKnowledgeBaseUrls);
  yield takeLatest(Actions.loadKnowledgeBaseMarkdownFilesRequest, loadKnowledgeBaseMarkdownFiles);
  yield takeLatest(Actions.updateKnowledgeBaseMarkdownFileRequest, updateKnowledgeBaseMarkdownFile);
  yield takeLatest(
    Actions.loadTestKnowledgeBaseMarkdownFilesRequest,
    loadTestKnowledgeBaseMarkdownFiles,
  );
  yield takeLatest(
    Actions.updateTestKnowledgeBaseMarkdownFileRequest,
    updateTestKnowledgeBaseMarkdownFile,
  );
  yield takeLatest(Actions.loadTestKnowledgeBasesRequest, loadTestKnowledgeBases);

  yield takeLatest(Actions.loadConversationRequest, loadConversation);
  yield takeLatest(Actions.setBotAssessmentStateRequest, setBotAssessmentState);
  yield takeLatest(Actions.setRerunConversationIdRequest, setRerunConversationId);
}
